import {
	forwardRef,
	createContext,
	useContext,
	FC,
	ReactNode,
	HTMLProps,
	ButtonHTMLAttributes,
	ComponentProps
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import styled from 'styled-components';
import { useMergeRefs, FloatingPortal, FloatingFocusManager, FloatingArrow, FloatingOverlay } from '@floating-ui/react';
import { AnimatePresence, motion, AnimationProps, HTMLMotionProps } from 'motion/react';
import useFloatingElement, { FloatingElementOptions } from '../utils/use-floating-element';

type ContextType = ReturnType<typeof useFloatingElement> | null;

const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = () => {
	const context = useContext(PopoverContext);

	if (context == null) {
		throw new Error('Popover components must be wrapped in <Popover />');
	}

	return context;
};

type RootProps = {
	children: ReactNode;
} & FloatingElementOptions;

/**
 * @title Popover (Root)
 *
 * @description
 * A Popover is a pop-up box that appears when the user clicks on an element. It can be used to display additional information about a component or to display a list of actions.
 * The Popover component is a wrapper that provides context to the other components such as the trigger, content, and arrow and more.
 *
 * @param isModal - Determines if focus is “modal”, meaning focus is fully trapped inside the floating element and outside content cannot be accessed. This includes screen reader virtual cursors. See https://floating-ui.com/docs/floatingfocusmanager#modal for more information.
 * @param defaultOpen - Determines if the Popover should be open by default.
 * @param onOpenChange - Callback that is called when the Popover is opened or closed.
 * @param isOpen - Determines if the Popover is open or closed. This is useful if you want to control the Popover state manually.
 * @param placement - Determines where the Popover should be placed relative to the trigger element.
 * @param dissmissOptions - Customize the dismiss functionality. See https://floating-ui.com/docs/dismiss for more information.
 * @param middlewareOptions - Customize the middleware functions. See https://floating-ui.com/docs/middleware for more information.
 *
 * @example
 * <Popover.Root>
 * 		<Popover.Trigger>Trigger</Popover.Trigger>
 *  	<Popover.Portal>
 *  			<Popover.Overlay />
 * 				<Popover.Content>
 * 						<Popover.Heading>Heading</Popover.Heading>
 *						<Popover.Description>Description</Popover.Description>
 *						<Popover.Close>Close</Popover.Close>
 * 				</Popover.Content>
 * 		<Popover.Portal>
 * </Popover.Root>
 */
const Root: FC<RootProps> = ({ children, isModal = false, ...restOptions }) => {
	const popover = useFloatingElement({ isModal, ...restOptions });

	return <PopoverContext.Provider value={popover}>{children}</PopoverContext.Provider>;
};

type TriggerProps = {
	children: ReactNode;
	asChild?: boolean;
};

const Trigger = forwardRef<HTMLElement, HTMLProps<HTMLElement> & TriggerProps>(
	({ children, asChild, ...props }, propRef) => {
		const context = usePopoverContext();
		const ref = useMergeRefs([context.refs.setReference, propRef]);

		if (asChild) {
			return (
				<Slot
					ref={ref}
					data-state={context.isOpen ? 'open' : 'closed'} // Allows for targeting the trigger with CSS
					{...context.getReferenceProps(props)}
				>
					{children}
				</Slot>
			);
		}

		return (
			<button
				ref={ref}
				type="button"
				data-state={context.isOpen ? 'open' : 'closed'} // Allows for targeting the trigger with CSS
				{...context.getReferenceProps(props)}
			>
				{children}
			</button>
		);
	}
);

Trigger.displayName = 'PopoverTrigger';

type PortalProps = {
	id?: string;
	children: ReactNode;
	root?: HTMLElement | null;
};

const Portal = ({ children, id, root }: PortalProps) => {
	const { context } = usePopoverContext();
	return (
		<FloatingPortal id={id} root={root}>
			{context.open && children}
		</FloatingPortal>
	);
};

const Backdrop = styled(FloatingOverlay)`
	display: flex;
	place-items: center;
	z-index: 1;
`;

const AnimatedBackdrop = motion(Backdrop);

type BackdropProps = HTMLMotionProps<'div'> & ComponentProps<typeof FloatingOverlay>;

const Overlay = forwardRef<HTMLDivElement, BackdropProps>(({ children, ...props }, ref) => {
	return (
		<AnimatePresence>
			<AnimatedBackdrop
				initial={{ opacity: 0 }}
				animate={{ opacity: 1, transition: { duration: 0.25 } }}
				exit={{ opacity: 0, transition: { duration: 0.15 } }}
				style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
				{...props}
				ref={ref}
			/>
		</AnimatePresence>
	);
});

Overlay.displayName = 'PopoverOverlay';

const StyledContent = styled(motion.div)`
	background-color: var(--floating-element-bg-color);
	z-index: 2;
`;

type FloatingFocusManagerProps = Pick<React.ComponentProps<typeof FloatingFocusManager>, 'initialFocus' | 'order'>;
type ContentProps = FloatingFocusManagerProps & HTMLProps<HTMLDivElement> & AnimationProps;

/**
 * @title Popover Content
 * @description The PopoverContent component is used to render the content of the Popover.
 *
 * @param initialFocus - The element that should receive focus when the Popover is opened. Can be disabled by passing a negative number. Can be any valid query selector string
 * @param order - The order in which the Popover content and trigger should be focused when tabbing. Default order: ['content', 'reference', floating']
 *
 * Because the Popover is build on using Framer Motion it is possible to pass in animation props to the Content component. This allows for custom animations to be used.
 * @param initial - The initial animation state of the Popover content.
 * @param animate - The animation state of the Popover content when it is open.
 * @param exit - The animation state of the Popover content when it is closed.
 * @param transition - The animation transition of the Popover content.
 */
const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, initialFocus, order, ...props }, propRef) => {
	const { context: floatingContext, setIsOpen, ...context } = usePopoverContext();
	const ref = useMergeRefs([context.refs.setFloating, propRef]);

	const xOffset = `${context.x.toFixed(0)}px`;
	const yOffset = `${context.y.toFixed(0)}px`;

	const popoverOffsets = {
		'--popover-x-offset': xOffset,
		'--popover-y-offset': yOffset
	} as React.CSSProperties;

	return (
		<AnimatePresence>
			<FloatingFocusManager
				context={floatingContext}
				modal={context.isModal}
				initialFocus={initialFocus}
				order={order}
			>
				<StyledContent
					initial={{ opacity: 0 }}
					animate={{ opacity: 1, transition: { duration: 0.25 } }}
					exit={{ opacity: 0, transition: { duration: 0.15 } }}
					ref={ref}
					style={{
						...context.floatingStyles,
						...props.style,
						...popoverOffsets
					}}
					{...context.getFloatingProps(props)}
				>
					{children}
				</StyledContent>
			</FloatingFocusManager>
		</AnimatePresence>
	);
});

Content.displayName = 'PopoverContent';

const Heading = forwardRef<HTMLHeadingElement, HTMLProps<HTMLHeadingElement>>(({ children, ...props }, ref) => {
	return (
		<h2 {...props} ref={ref}>
			{children}
		</h2>
	);
});

Heading.displayName = 'PopoverHeading';

const Description = forwardRef<HTMLParagraphElement, HTMLProps<HTMLParagraphElement>>(({ children, ...props }, ref) => {
	return (
		<p {...props} ref={ref}>
			{children}
		</p>
	);
});

Description.displayName = 'PopoverDescription';

const Close = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>((props, ref) => {
	const { setIsOpen } = usePopoverContext();

	return (
		<button
			type="button"
			ref={ref}
			{...props}
			onClick={event => {
				props.onClick?.(event);
				setIsOpen(false);
			}}
		/>
	);
});

Close.displayName = 'PopoverClose';

const StyledArrow = styled(FloatingArrow)`
	fill: var(--floating-element-bg-color);

	& path:first-of-type {
		stroke: var(--floating-element-bg-color);
	}

	& path:last-of-type {
		stroke: var(--floating-element-bg-color);
	}
`;

type ArrowProps = HTMLProps<SVGSVGElement> & {
	staticOffset?: number;
	tipRadius?: number;
};

/**
 * @title Arrow
 * @description The PopoverArrow component is used to render the arrow of the Popover providing a visual connection between the Popover and the trigger element
 * @param tipRadius - The radius (rounding) of the arrow tip. Defaults to 0.
 * @param staticOffset - A static offset override of the arrow from the floating element edge. Often desirable if the floating element is smaller than the reference element along the relevant axis and has an edge alignment ('start'/'end').
 */
const Arrow = forwardRef<SVGSVGElement, ArrowProps>(({ className = '', staticOffset, tipRadius }, propRef) => {
	const { context, arrowRef } = usePopoverContext();
	const ref = useMergeRefs([propRef, arrowRef]);

	return (
		<StyledArrow
			ref={ref}
			context={context}
			className={className}
			tipRadius={tipRadius}
			staticOffset={staticOffset}
		/>
	);
});

Arrow.displayName = 'PopoverArrow';

export default {
	Root,
	Trigger,
	Content,
	Heading,
	Description,
	Close,
	Arrow,
	Overlay,
	Portal
};
